import { DateRange } from 'rewire-common';
import { value, DataSignal } from 'rewire-core';
import deepequals from 'fast-deep-equal';
import batchSummaryModel from '../batch/BatchSummaryModel';
import createBatchModel from '../batch/CreateBatchModel';
import {
  IRouteManager,
  ProductName,
  ReasonCategory,
  ReasonCategories,
  ScheduleFilterInput,
  ScheduleSort,
  UdfDocumentType,
  SortInput,
  IScheduleGridOptions,
} from '../DataTypes';
import reportModel from '../report/ReportModel';
import { history } from '../RouterWithHistory';
import Routes from '../Routes';
import scheduleControlsModel from '../schedule/ScheduleControlsModel';
import scheduleModel from '../schedule/ScheduleModel';
import summaryListModel from '../timecard/SummaryListModel';
import timecardModel from '../timecard/TimecardModel';
import timecardSummaryModel from '../timecard/TimecardSummaryModel';
import timecardTestModel, { TestViewType } from '../test/TimecardTestModel';
import timecardTestListModel from '../test/TimecardTestListModel';
import timeOffModel from '../timeoff/TimeOffModel';
import mainDateRangeNavModel from '../ui/MainDateRangeNavModel';
import { durationToDays } from '../ui/DateRangeNavModel';
import { getUTCFromDateSelector } from '../ui/MainCustomFormDatePicker';
import { isSame } from '../utility/DateUtils';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';
import { getUdfFilterLookupField, getUdfSortLookupString } from '../utility/DataConverters';
import mainPageModel from './MainPageModel';
import createAdjustmentsBatchModel from '../batch/CreateAdjustmentsBatchModel';

export interface IRouteManagerState {
  route?: string;
  effective?: DateRange;
  areaId?: string;
  groupId?: string;
  viewId?: string;
  filters?: ScheduleFilterInput;
  groups?: string[];
  sorts?: ScheduleSort;
  scheduleGridOptions?: IScheduleGridOptions;
}

const _state: IRouteManagerState = {
  route: undefined,
  effective: undefined,
  areaId: undefined,
  groupId: undefined,
  viewId: undefined,
  filters: {},
  groups: [],
  sorts: {},
  scheduleGridOptions: undefined,
};

// ==============================================================================
// ==============================================================================

export abstract class RouteManagerBase implements IRouteManager {
  static _current: DataSignal<IRouteManager | undefined> = value(undefined);
  static get current(): IRouteManager {
    return RouteManagerBase._current()!;
  }
  static set current(routeManager: IRouteManager) {
    RouteManagerBase._current(routeManager);
  }
  static updateRoute() {
    _state.route = location.pathname;
  }

  protected _isLoading: DataSignal<boolean>;
  protected otherState: any;
  protected abstract load(newState: IRouteManagerState): Promise<void>;
  abstract get path(): string;
  abstract get documentType(): UdfDocumentType;
  abstract get access(): boolean;

  constructor(
    public readonly title: string,
    otherState?: any,
  ) {
    this._isLoading = value(false);
    if (otherState) this.otherState = otherState;
  }

  get isLoading(): boolean {
    return this._isLoading();
  }
  get route(): string | undefined {
    return _state.route;
  }
  get effective(): DateRange | undefined {
    return _state.effective;
  }
  get areaId(): string | undefined {
    return _state.areaId;
  }
  get groupId(): string | undefined {
    return _state.groupId;
  }
  get viewId(): string | undefined {
    return _state.viewId;
  }
  get allFilters(): ScheduleFilterInput | undefined {
    return _state.filters;
  }
  get allGroups(): string[] | undefined {
    return _state.groups;
  }
  get allSorts(): ScheduleSort | undefined {
    return _state.sorts;
  }
  get scheduleGridOptions(): IScheduleGridOptions | undefined {
    return _state.scheduleGridOptions;
  }

  get filters(): ScheduleFilterInput | undefined {
    const permittedFilters = this.getPermittedScheduleFilterInput(_state.filters);
    if (permittedFilters?.custom) {
      const customFilters = permittedFilters.custom.slice();
      for (let i = 0; i < customFilters.length; i++) {
        const documentType = udfsSubscriptionService.subscriptionModel.getUdfDocumentType(customFilters[i].field);
        if (!documentType) continue;
        customFilters[i] = getUdfFilterLookupField(customFilters[i], documentType);
      }
      permittedFilters.custom = customFilters;
    }

    return permittedFilters;
  }

  get groups(): string[] | undefined {
    let groups: string[] | undefined;
    if (_state.groups) {
      for (const group of _state.groups) {
        if (!this.permittedGroups.includes(group)) continue;
        if (!groups) groups = [];
        groups.push(group);
      }
    }
    return groups;
  }

  get sorts(): ScheduleSort | undefined {
    const permittedSorts = this.getPermittedScheduleSort(_state.sorts);
    return RouteManagerBase.getScheduleSortForQuery(permittedSorts);
  }

  static getScheduleSortForQuery(sorts?: ScheduleSort): ScheduleSort | undefined {
    if (!sorts) return;
    const sortsForQuery = { ...sorts } as ScheduleSort;
    if (sortsForQuery?.custom) {
      const customSorts = sortsForQuery.custom.map((customSort: SortInput) => ({ ...customSort }) as SortInput);
      for (const sortInput of customSorts) {
        const documentType = udfsSubscriptionService.subscriptionModel.getUdfDocumentType(sortInput.field);
        if (!documentType) continue;
        sortInput.field = getUdfSortLookupString(sortInput.field, documentType);
      }
      sortsForQuery.custom = customSorts;
    }
    return sortsForQuery;
  }

  get navigationPath() {
    const { areaId, groupId, viewId } = this;
    const title = viewId && mainPageModel.viewMap[viewId]?.name;
    if (title) return title;

    const areaName = areaId && mainPageModel.areaMap[areaId]?.name;
    const groupName = groupId && mainPageModel.groupMap[groupId]?.name;
    return areaName && groupName && `${areaName} » ${groupName}`;
  }

  getPermittedScheduleFilterInput(filters?: ScheduleFilterInput): ScheduleFilterInput | undefined {
    const scheduleFilterInput: any = {};
    if (filters) {
      for (const key of Object.keys(filters) as (keyof ScheduleFilterInput)[]) {
        if (key === 'custom') {
          const customFilters = filters[key];
          const udfFilters = customFilters?.filter((fieldFilter) => this.permittedFilters.includes(fieldFilter.field));
          if (udfFilters?.length) {
            scheduleFilterInput[key] = udfFilters;
          }
        } else {
          if (!this.permittedFilters.includes(key)) continue;
          scheduleFilterInput[key] = filters[key];
        }
      }
    }

    if (!Object.keys(scheduleFilterInput).length) {
      return undefined;
    }

    return scheduleFilterInput;
  }

  getPermittedScheduleSort(sorts?: ScheduleSort): ScheduleSort | undefined {
    const scheduleSorts: ScheduleSort = {};
    if (sorts) {
      for (const key of Object.keys(sorts) as (keyof ScheduleSort)[]) {
        if (key === 'custom') {
          const customSorts = sorts[key];
          const udfSorts = customSorts?.filter((sortInput) => this.permittedSorts.includes(sortInput.field));
          if (udfSorts?.length) {
            scheduleSorts[key] = udfSorts;
          }
        } else {
          if (!this.permittedSorts.includes(key)) continue;
          scheduleSorts[key] = sorts[key];
        }
      }
    }

    if (!Object.keys(scheduleSorts).length) {
      return undefined;
    }

    return scheduleSorts;
  }

  protected setTitle(title?: string) {
    const path = this.navigationPath;
    const newTitle = `${title ?? this.title}${(path && ` - ${path}`) || ''} - ${ProductName}`;
    if (window.document.title !== newTitle) {
      window.document.title = newTitle;
    }
  }

  protected _shouldLoad(newState: IRouteManagerState): boolean {
    if (!newState.effective) return false;
    if (!isSame(this.effective, newState.effective)) return true;
    if (this.route !== newState.route) return true;
    if (this.areaId !== newState.areaId) return true;
    if (this.groupId !== newState.groupId) return true;
    if (this.viewId !== newState.viewId) return true;
    if (!deepequals(_state.filters, newState.filters)) return true;
    if (!deepequals(_state.groups, newState.groups)) return true;
    if (!deepequals(_state.sorts, newState.sorts)) return true;
    if (!deepequals(_state.scheduleGridOptions, newState.scheduleGridOptions)) return true;

    return false;
  }

  protected _assignState(newState: IRouteManagerState): void {
    _state.route = newState.route;
    _state.areaId = newState.areaId;
    _state.groupId = newState.groupId;
    _state.viewId = newState.viewId;
    _state.effective = newState.effective;
    _state.filters = newState.filters;
    _state.groups = newState.groups;
    _state.sorts = newState.sorts;
    _state.scheduleGridOptions = newState.scheduleGridOptions;
  }

  protected _getNewState(loadData: URLSearchParams): IRouteManagerState {
    const route = location.pathname;
    let effective: DateRange | undefined;
    const areaId = this.get(loadData, 'areaId');
    const groupId = this.get(loadData, 'groupId');
    const viewId = this.get(loadData, 'viewId');
    const filters = this.getObject<ScheduleFilterInput>(loadData, 'filters');
    const groups = this.getObject<string[]>(loadData, 'groups');
    const sorts = this.getObject<ScheduleSort>(loadData, 'sorts');
    const scheduleGridOptions = this.getObject<IScheduleGridOptions>(loadData, 'scheduleGridOptions');

    const eff = this.get(loadData, 'effective');
    const daysAllowed = this.dateDurationDays || 1;
    if (eff) {
      // create effective range from url
      const parsedEffective = JSON.parse(eff);
      let temp = new DateRange(parsedEffective.start, parsedEffective.end);
      if (temp.isValid) {
        if (temp.length > daysAllowed) {
          temp = new DateRange(temp.start, temp.start.add(daysAllowed - 1));
        }
        effective = temp;
      }
    } else {
      // create effective range from date selector and/or dateDuration saved in local storage, otherwise, default start date to todays date.
      let days = durationToDays[mainPageModel.dateDuration];
      if (days > daysAllowed) {
        days = daysAllowed;
      }
      const dateSelectorDate = getUTCFromDateSelector(mainPageModel.dateSelector);
      effective = new DateRange(dateSelectorDate, dateSelectorDate.add(days - 1));
    }

    return {
      route,
      effective,
      areaId,
      groupId,
      viewId,
      filters,
      groups,
      sorts,
      scheduleGridOptions,
    };
  }

  protected get(loadData: URLSearchParams, key: string): string | undefined {
    const value = loadData.get(key) as string | null;
    return value || undefined;
  }

  protected getObject<T>(loadData: URLSearchParams, key: string): T | undefined {
    const value = loadData.get(key) as string | null;
    return value ? (JSON.parse(value) as T) : undefined;
  }

  async update() {
    const loadData = new URLSearchParams(location.search);
    const newState = this._getNewState(loadData);
    if (this._shouldLoad(newState)) {
      this._isLoading(true);
      const prevState = this.cloneState();
      this._assignState(newState);
      mainDateRangeNavModel.load({
        effective: this.effective!,
        maxDays: this.dateDurationDays,
        allowMonth: this.allowMonth,
        allowQuarters: this.allowQuarters,
      });
      await scheduleControlsModel.load();
      if (this.access) {
        await this.load(prevState);
      }
      this._isLoading(false);
    }
    if (this.title) this.setTitle();
  }

  protected cloneState() {
    const state = Object.assign({}, _state, this.otherState);
    state.filters = _state.filters && Object.assign({}, _state.filters);
    state.groups = _state.groups && [..._state.groups];
    state.sorts = _state.sorts && Object.assign({}, _state.sorts);
    return state;
  }

  createLinkFor(data?: any): string {
    const linkData = Object.assign({}, { effective: _state.effective }, this.otherState, data);
    if (!data.selectedBatchId) delete linkData.selectedBatchId; // only do this when not explicitly specified not useful to Areas / Groups
    const path = this.parseParameters(linkData);
    return path;
  }

  createPayPlanLinkFor(data?: any): string {
    const linkData = Object.assign({}, _state, this.otherState, data);
    const path = this.parseParameters(linkData);
    return path;
  }

  private parseParameters(linkData: Record<string, unknown>) {
    const params = new URLSearchParams();

    for (const key in linkData) {
      if (key === 'route') continue;

      // eslint-disable-next-line no-prototype-builtins
      if (Object.hasOwn(linkData, key)) {
        const element = linkData[key];
        if (element === undefined || element === null) continue;

        let sValue: string;
        if (Array.isArray(element)) {
          if (!element.length) continue;
          sValue = JSON.stringify(element);
        } else if (typeof element === 'object') {
          sValue = JSON.stringify(element);
          if (sValue === '{}') continue;
        } else {
          sValue = element as string;
        }
        params.set(key, sValue);
      }
    }
    params.sort();
    const path = `${this.path}?${params.toString()}`;
    return path;
  }

  navigateTo(data?: any): void {
    const route = this.createPayPlanLinkFor(data);
    history.push(route);
  }

  // default unless overridden
  get permittedFilters(): string[] {
    return [
      'areaIds',
      'groupIds',
      'occupationIds',
      'generatorIds',
      'assignedEmployeeIds',
      'templateIds',
      'reasonIds',
      'employeeIds',
      ...udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees']),
    ];
  }

  get permittedGroups(): string[] {
    return this.permittedSorts;
  }

  get permittedSorts(): string[] {
    return [
      'area',
      'group',
      'occupation',
      'generator',
      'employee',
      ...udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees']),
    ];
  }

  get reasonFilterCategories(): ReasonCategory[] {
    return Object.values(ReasonCategories);
  }

  get allowMonth(): boolean {
    return false;
  }

  get allowQuarters(): boolean {
    return false;
  }

  get dateDurationDays(): number {
    return 14;
  }
}

// ==============================================================================
// ==============================================================================

export abstract class PlanRouteManagerBase extends RouteManagerBase {
  constructor(public readonly title: string) {
    super(title);
  }

  get documentType(): UdfDocumentType {
    return 'employees';
  } // no jobs type exists yet
  get access(): boolean {
    return mainPageModel.scheduleAccess;
  }

  get permittedFilters(): string[] {
    return [
      'areaIds',
      'groupIds',
      'occupationIds',
      'generatorIds',
      'assignedEmployeeIds',
      'employeeIds',
      'templateIds',
      'reasonIds',
      'search',
      'levels',
      ...udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees']),
    ];
  }

  get permittedSorts(): string[] {
    return [
      'area',
      'group',
      'occupation',
      'generator',
      'employee',
      ...udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees']),
    ];
  }
}

// ==============================================================================
// ==============================================================================

class JobScheduleRouteManager extends PlanRouteManagerBase {
  constructor() {
    super('Job Schedule');
  }

  get path(): string {
    return Routes.JobSchedule;
  }

  async load(_prevState: IRouteManagerState) {
    await scheduleModel.load('Job');
  }
}

export const jobScheduleRouteManager = new JobScheduleRouteManager();

// ==============================================================================
// ==============================================================================

class EmployeeScheduleRouteManager extends PlanRouteManagerBase {
  constructor() {
    super('Employee Schedule');
  }

  get path(): string {
    return Routes.EmployeeSchedule;
  }

  async load(_prevState: IRouteManagerState) {
    await scheduleModel.load('Employee');
  }
}

export const employeeScheduleRouteManager = new EmployeeScheduleRouteManager();

// ==============================================================================
// ==============================================================================

export abstract class TimeOffRouteManagerBase extends RouteManagerBase {
  constructor(public readonly title: string) {
    super(title);
  }

  get documentType(): UdfDocumentType {
    return 'timeofftypes';
  }
  get access(): boolean {
    return mainPageModel.timeOffRequestAccess;
  }

  get permittedFilters(): string[] {
    return [
      'areaIds',
      'groupIds',
      'occupationIds',
      'generatorIds',
      'reasonIds',
      'employeeIds',
      'timeOffStatus',
      ...udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees']),
    ];
  }

  get permittedSorts(): string[] {
    return [
      'area',
      'group',
      'occupation',
      'employee',
      'generator',
      ...udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees']),
    ];
  }

  get dateDurationDays(): number {
    return 28;
  }
}

// ==============================================================================
// ==============================================================================

class TimeOffCalendarRouteManager extends TimeOffRouteManagerBase {
  constructor() {
    super('Time Off Calendar');
  }

  get path(): string {
    return Routes.TimeOffCalendar;
  }

  async load(_prevState: IRouteManagerState) {
    await timeOffModel.load('Calendar');
  }
}

export const timeOffCalendarRouteManager = new TimeOffCalendarRouteManager();

// ==============================================================================
// ==============================================================================

class TimeOffListRouteManager extends TimeOffRouteManagerBase {
  constructor() {
    super('Time Off List');
  }

  get path(): string {
    return Routes.TimeOffList;
  }

  async load(_prevState: IRouteManagerState) {
    await timeOffModel.load('List');
  }
}

export const timeOffListRouteManager = new TimeOffListRouteManager();

// ==============================================================================
// ==============================================================================

const payAndSummaryOtherState = {
  selectedEmployeeId: undefined,
  selectedBatchId: undefined,
};

abstract class PayRouteManagerBase extends RouteManagerBase {
  constructor(public readonly title: string) {
    super(title, payAndSummaryOtherState);
  }

  get access(): boolean {
    return mainPageModel.timecardAccess;
  }

  get permittedFilters(): string[] {
    return [
      'timecardStatus',
      'messageLevels',
      'inBatch',
      'adjustments',
      ...udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees']),
    ];
  }

  get permittedSorts(): string[] {
    return [
      'area',
      'group',
      'occupation',
      'generator',
      'employee',
      // ...udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees'])
    ];
  }
}

// ==============================================================================
// ==============================================================================

class PayRouteManager extends PayRouteManagerBase {
  constructor() {
    super('');
  }

  get path(): string {
    return Routes.Timecard;
  }
  get documentType(): UdfDocumentType {
    return 'employees';
  }

  async load(prevState: IRouteManagerState) {
    scheduleControlsModel.sortModel.mode = 'single';
    await summaryListModel.load(this.selectedEmployeeId, {
      prevState: prevState,
      currState: this.cloneState(),
      effective: this.effective,
      route: this.route,
      filters: this.filters,
      sorts: this.sorts,
    });
    await timecardModel.load(this.selectedBatchId, this.filters);
  }

  get permittedFilters(): string[] {
    return [
      'areaIds',
      'groupIds',
      'occupationIds',
      'generatorIds',
      'templateIds',
      'reasonIds',
      'employeeIds',
      'messageLevels',
      'timecardStatus',
      'inBatch',
      'adjustments',
      'search',
      'onlyPayable',
      ...udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees']),
    ];
  }

  get permittedSorts(): string[] {
    return ['employee', 'employeeCode'];
  }

  _getNewState(loadData: URLSearchParams): IRouteManagerState {
    const newState = super._getNewState(loadData) as any;
    newState.selectedEmployeeId = this.get(loadData, 'selectedEmployeeId');
    newState.selectedBatchId = this.get(loadData, 'selectedBatchId');
    return newState;
  }

  _assignState(newState: IRouteManagerState) {
    super._assignState(newState);
    this.selectedEmployeeId = (newState as any).selectedEmployeeId;
    this.selectedBatchId = (newState as any).selectedBatchId;
  }

  _shouldLoad(newState: IRouteManagerState): boolean {
    if (super._shouldLoad(newState)) return true;
    if (this.selectedEmployeeId !== (newState as any).selectedEmployeeId) return true;
    if (this.selectedBatchId !== (newState as any).selectedBatchId) return true;
    return false;
  }

  get selectedEmployeeId(): string | undefined {
    return this.otherState.selectedEmployeeId;
  }
  set selectedEmployeeId(itemId: string | undefined) {
    this.otherState.selectedEmployeeId = itemId;
  }
  get selectedBatchId(): string | undefined {
    return this.otherState.selectedBatchId;
  }
  set selectedBatchId(itemId: string | undefined) {
    this.otherState.selectedBatchId = itemId;
  }
}

export const payRouteManager = new PayRouteManager();

// ==============================================================================
// ==============================================================================

class TimecardSummaryRouteManager extends PayRouteManagerBase {
  constructor() {
    super('Timecard Summary');
  }

  get path(): string {
    return Routes.TimecardSummary;
  }
  get documentType(): UdfDocumentType {
    return 'timecards';
  }

  async load(prevState: IRouteManagerState) {
    scheduleControlsModel.sortModel.mode = 'single';
    scheduleControlsModel.groupModel.mode = 'single';
    await timecardSummaryModel.load(prevState);
  }

  get permittedFilters(): string[] {
    return [
      'areaIds',
      'groupIds',
      'occupationIds',
      'generatorIds',
      'templateIds',
      'reasonIds',
      'employeeIds',
      'messageLevels',
      'timecardStatus',
      'inBatch',
      'adjustments',
      'search',
      ...udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees']),
    ];
  }

  get permittedSorts(): string[] {
    return ['employee', 'employeeCode'];
  }

  get permittedGroups(): string[] {
    return [
      'area',
      'group',
      'occupation',
      'generator',
      // ...udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees'])
    ];
  }
}

export const timecardSummaryRouteManager = new TimecardSummaryRouteManager();

// ==============================================================================
// ==============================================================================

class AdjustmentsBatchRouteManager extends RouteManagerBase {
  constructor() {
    super('Adjustments Batch', {});
  }

  get path(): string {
    return Routes.AdjustmentsBatch;
  }
  get documentType(): UdfDocumentType {
    return 'batches';
  }
  get access(): boolean {
    return mainPageModel.batchAccess && mainPageModel.canAddBatches;
  }

  async load(prevState: IRouteManagerState) {
    await createAdjustmentsBatchModel.load(prevState);
  }

  get permittedFilters(): string[] {
    return [];
  }

  get permittedSorts(): string[] {
    return [];
  }
}

export const adjustmentsBatchRouteManager = new AdjustmentsBatchRouteManager();

// ==============================================================================
// ==============================================================================

class BatchRouteManager extends RouteManagerBase {
  constructor() {
    super('Batch', {});
  }

  get path(): string {
    return Routes.Batch;
  }
  get documentType(): UdfDocumentType {
    return 'batches';
  }
  get access(): boolean {
    return mainPageModel.batchAccess && mainPageModel.canAddBatches;
  }

  async load(prevState: IRouteManagerState) {
    await createBatchModel.load(prevState);
  }

  get permittedFilters(): string[] {
    return [];
  }

  get permittedSorts(): string[] {
    return [];
  }
}

export const batchRouteManager = new BatchRouteManager();

// ==============================================================================
// ==============================================================================

class BatchSummaryRouteManager extends RouteManagerBase {
  constructor() {
    super('Batch Summary');
  }

  get path(): string {
    return Routes.BatchSummary;
  }
  get documentType(): UdfDocumentType {
    return 'batches';
  }
  get access(): boolean {
    return mainPageModel.batchAccess;
  }

  async load(prevState: IRouteManagerState) {
    await batchSummaryModel.load(prevState);
  }

  get permittedFilters(): string[] {
    return [
      // 'areaIds',
      // 'groupIds',
      // 'occupationIds',
      // 'generatorIds',
      // 'templateIds',
      // 'reasonIds',
      // 'employeeIds',
      // 'batchStatus'
      'batchIds',
    ];
  }

  get permittedSorts(): string[] {
    return [];
  }
}

export const batchSummaryRouteManager = new BatchSummaryRouteManager();

// ==============================================================================
// ==============================================================================

class ReportRouteManager extends RouteManagerBase {
  constructor() {
    super('Report', { selectedReportId: undefined, options: undefined, reportSecondaryFilters: undefined });
  }

  get path(): string {
    return Routes.Report;
  }
  get documentType(): UdfDocumentType {
    return 'documents';
  } // not document types for reports yet
  get access(): boolean {
    return mainPageModel.reportAccess;
  }

  async load(prevState: IRouteManagerState) {
    scheduleControlsModel.sortModel.mode = 'single';
    await reportModel.load(prevState);
  }

  get permittedFilters(): string[] {
    return reportModel.permittedFilters();
  }

  get permittedGroups(): string[] {
    return reportModel.permittedGroups();
  }

  get permittedSorts(): string[] {
    return reportModel.permittedSorts();
  }

  get permittedReportSecondaryFilters(): string[] {
    return reportModel.permittedSecondaryFilters();
  }

  isCustomSort(sortBy: string): boolean {
    return ![
      'area',
      'group',
      'generator', // shift pattern
      'occupation',
      'employee',
      'accountCode',
    ].includes(sortBy);
  }

  get reasonFilterCategories(): ReasonCategory[] {
    return reportModel.reasonFilterCategories();
  }

  get allowMonth(): boolean {
    return reportModel.allowMonth();
  }

  get allowQuarters(): boolean {
    return reportModel.allowQuarters();
  }

  get dateDurationDays(): number {
    return reportModel.dateDurationDays();
  }

  _getNewState(loadData: URLSearchParams): IRouteManagerState {
    const newState = super._getNewState(loadData);
    (newState as any).selectedReportId = this.get(loadData, 'selectedReportId');
    (newState as any).options = this.getObject<any>(loadData, 'options');
    (newState as any).formats = this.getObject<any>(loadData, 'formats');
    (newState as any).reportSecondaryFilters = this.getObject<any>(loadData, 'reportSecondaryFilters');
    return newState;
  }

  get navigationPath() {
    const path = super.navigationPath;
    const reportName = reportModel.selectedItem?.name;

    return (reportName ? (path ? `${path} » ${reportName}` : reportName) : path) ?? '';
  }

  _assignState(newState: IRouteManagerState) {
    super._assignState(newState);
    this.selectedReportId = (newState as any).selectedReportId;
    this.options = (newState as any).options;
    this.formats = (newState as any).formats;
    this.reportSecondaryFilters = (newState as any).reportSecondaryFilters;
  }

  _shouldLoad(newState: IRouteManagerState): boolean {
    if (super._shouldLoad(newState)) return true;
    return (
      this.selectedReportId !== (newState as any).selectedReportId ||
      !deepequals(this.options, (newState as any).options) ||
      !deepequals(this.formats, (newState as any).formats) ||
      !deepequals(this.reportSecondaryFilters, (newState as any).reportSecondaryFilters)
    );
  }

  get selectedReportId(): string | undefined {
    return this.otherState.selectedReportId;
  }
  set selectedReportId(itemId: string | undefined) {
    this.otherState.selectedReportId = itemId;
  }

  get options(): any {
    return this.otherState.options;
  }
  set options(value: any) {
    this.otherState.options = value;
  }

  get formats(): any {
    return this.otherState.formats;
  }
  set formats(value: any) {
    this.otherState.formats = value;
  }

  get reportSecondaryFilters(): any {
    return this.otherState.reportSecondaryFilters;
  }
  set reportSecondaryFilters(value: any) {
    this.otherState.reportSecondaryFilters = value;
  }
}

export const reportRouteManager = new ReportRouteManager();

class AnalyticsRouteManager extends RouteManagerBase {
  constructor() {
    super('Analytics', { selectedReportId: undefined, options: undefined });
  }

  get path(): string {
    return Routes.Analytics;
  }

  get documentType(): UdfDocumentType {
    return 'documents';
  }

  get access(): boolean {
    return mainPageModel.analyticsAccess;
  }

  async load(prevState: IRouteManagerState) {
    // scheduleControlsModel.sortModel.mode = 'single';
    // await reportModel.load(prevState);
  }

  get permittedFilters(): string[] {
    return [];
  }

  get permittedGroups(): string[] {
    return [];
  }

  get permittedSorts(): string[] {
    return [];
  }

  isCustomSort(sortBy: string): boolean {
    return false;
  }

  get reasonFilterCategories(): ReasonCategory[] {
    return [];
  }

  get allowMonth(): boolean {
    return false;
  }

  get allowQuarters(): boolean {
    return false;
  }

  get dateDurationDays(): number {
    return 7;
  }

  _getNewState(loadData: URLSearchParams): IRouteManagerState {
    const newState = super._getNewState(loadData);
    (newState as any).selectedReportId = this.get(loadData, 'selectedReportId');
    (newState as any).options = this.getObject<any>(loadData, 'options');
    (newState as any).formats = this.getObject<any>(loadData, 'formats');
    return newState;
  }

  get navigationPath() {
    const path = super.navigationPath;
    const reportName = reportModel.selectedItem?.name;

    return (reportName ? (path ? `${path} » ${reportName}` : reportName) : path) ?? '';
  }

  _assignState(newState: IRouteManagerState) {
    super._assignState(newState);
    this.selectedReportId = (newState as any).selectedReportId;
    this.options = (newState as any).options;
    this.formats = (newState as any).formats;
  }

  _shouldLoad(newState: IRouteManagerState): boolean {
    if (super._shouldLoad(newState)) return true;
    return (
      this.selectedReportId !== (newState as any).selectedReportId ||
      !deepequals(this.options, (newState as any).options) ||
      !deepequals(this.formats, (newState as any).formats)
    );
  }

  get selectedReportId(): string | undefined {
    return this.otherState.selectedReportId;
  }
  set selectedReportId(itemId: string | undefined) {
    this.otherState.selectedReportId = itemId;
  }

  get options(): any {
    return this.otherState.options;
  }
  set options(value: any) {
    this.otherState.options = value;
  }

  get formats(): any {
    return this.otherState.formats;
  }
  set formats(value: any) {
    this.otherState.formats = value;
  }
}

export const analyticsRouteManager = new AnalyticsRouteManager();

// ==============================================================================
// ==============================================================================

class TimecardTestRouteManager extends RouteManagerBase {
  constructor() {
    super('', { selectedTimecardTestId: undefined, testView: 'Summary' });
  }

  get path(): string {
    return Routes.TimecardTest;
  }
  get documentType(): UdfDocumentType {
    return 'documents';
  }
  get access(): boolean {
    return mainPageModel.testAccess;
  }

  async load(prevState: IRouteManagerState) {
    const testView = this.testView ?? 'Summary';
    scheduleControlsModel.sortModel.mode = 'single';
    await timecardTestListModel.load(this.selectedTimecardTestId, {
      prevState: prevState,
      currState: this.cloneState(),
      effective: this.effective,
      route: this.route,
      filters: this.filters,
      sorts: this.sorts,
    });
    await timecardTestModel.load(this.selectedTimecardTestId, testView);
  }

  get permittedFilters(): string[] {
    return [
      'areaIds',
      'groupIds',
      'occupationIds',
      'generatorIds',
      'employeeIds',
      'effective',
      'status',
      'lastRanAfter',
      'tags',
      // ...udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees'])
    ];
  }

  get permittedSorts(): string[] {
    return ['employee', 'employeeCode'];
  }

  _getNewState(loadData: URLSearchParams): IRouteManagerState {
    const newState = super._getNewState(loadData) as any;
    newState.selectedTimecardTestId = this.get(loadData, 'selectedTimecardTestId');
    newState.testView = this.get(loadData, 'testView');
    return newState;
  }

  _assignState(newState: IRouteManagerState) {
    super._assignState(newState);
    this.selectedTimecardTestId = (newState as any).selectedTimecardTestId;
    this.testView = (newState as any).testView;
  }

  _shouldLoad(newState: IRouteManagerState): boolean {
    if (super._shouldLoad(newState)) return true;
    if (this.selectedTimecardTestId !== (newState as any).selectedTimecardTestId) return true;
    if (this.testView !== (newState as any).testView) return true;
    return false;
  }

  get selectedTimecardTestId(): string | undefined {
    return this.otherState.selectedTimecardTestId;
  }
  set selectedTimecardTestId(itemId: string | undefined) {
    this.otherState.selectedTimecardTestId = itemId;
  }

  get testView(): TestViewType | undefined {
    return this.otherState.testView;
  }
  set testView(testView: TestViewType | undefined) {
    this.otherState.testView = testView;
  }
}

export const timecardTestRouteManager = new TimecardTestRouteManager();

// Default
RouteManagerBase.current = jobScheduleRouteManager;
